/**
 * LabelFilter plugin - conditional composite filter for labels.
 *
 * This is a conditional filter (DD-6): Component returns null when loaded + no data.
 * Shows skeleton during isLoading to prevent layout shift.
 *
 * This filter has a composite value type: { labelIds: string[], labelFilter?: string }
 * because label filtering supports both specific label IDs and a filter mode
 * ("labeled" / "unlabeled").
 *
 * URL params: labelId (singular, multiple), labelFilter (single)
 * API params: labelId (same as URL), labelFilter (same as URL)
 * SavedView fields: labelIds, labelFilter
 *
 * @see docs/specifications/2026-03-10-plugin-filter-architecture.md (Section 4, DD-6)
 */

import React, { useMemo, useCallback } from "react";
import { FilterPlugin, FilterComponentProps } from "../types";
import { SavedViewFilters } from "../../../../../services/grantsApi";
import MultiSelectDropdown from "../../../../common/MultiSelectDropdown";
import { LabelCount } from "../../../../../types/grants";

/**
 * Composite value type for the label filter.
 * Combines specific label IDs with a filter mode.
 */
export interface LabelFilterValue {
  /** Selected label UUIDs */
  labelIds: string[];
  /** Filter mode: "labeled" | "unlabeled" | undefined (no mode) */
  labelFilter?: string;
}

/** Special option value for "has any label" filter */
const LABEL_FILTER_LABELED = "__labeled__";
/** Special option value for "has no label" filter */
const LABEL_FILTER_UNLABELED = "__unlabeled__";

/**
 * Label filter React component.
 * Conditional: returns null when loaded + no data (DD-6).
 * Shows skeleton during isLoading to prevent layout shift.
 */
const LabelFilterComponent = React.memo(function LabelFilterComponent(
  props: FilterComponentProps<LabelFilterValue>
) {
  const { value, onChange, facets, isLoading } = props;

  const labelCounts = facets?.labelCounts as LabelCount[] | undefined;

  const options = useMemo(() => {
    const baseOptions = (labelCounts ?? []).map((label) => ({
      value: label.id,
      label: label.name,
      count: label.count,
    }));

    if (baseOptions.length === 0) return baseOptions;

    return [
      { value: LABEL_FILTER_LABELED, label: "Tylko oznaczone", count: undefined },
      { value: LABEL_FILTER_UNLABELED, label: "Bez etykiety", count: undefined },
      ...baseOptions,
    ];
  }, [labelCounts]);

  // Build selected values array from composite value
  const selectedValues = useMemo(() => {
    const selected: string[] = [];
    if (value.labelFilter === "labeled") selected.push(LABEL_FILTER_LABELED);
    if (value.labelFilter === "unlabeled") selected.push(LABEL_FILTER_UNLABELED);
    selected.push(...value.labelIds);
    return selected;
  }, [value]);

  // Handle selection changes with mutual exclusion logic
  const handleChange = useCallback(
    (newSelected: string[]) => {
      const hasLabeled = newSelected.includes(LABEL_FILTER_LABELED);
      const hasUnlabeled = newSelected.includes(LABEL_FILTER_UNLABELED);
      const labelIds = newSelected.filter(
        (v) => v !== LABEL_FILTER_LABELED && v !== LABEL_FILTER_UNLABELED
      );

      // Mutual exclusion: labeled/unlabeled are exclusive with each other and with specific labels
      let labelFilter: string | undefined;
      if (hasLabeled && !selectedValues.includes(LABEL_FILTER_LABELED)) {
        // User just selected "labeled" - clear unlabeled and specific labels
        labelFilter = "labeled";
        onChange({ labelIds: [], labelFilter });
        return;
      }
      if (hasUnlabeled && !selectedValues.includes(LABEL_FILTER_UNLABELED)) {
        // User just selected "unlabeled" - clear labeled and specific labels
        labelFilter = "unlabeled";
        onChange({ labelIds: [], labelFilter });
        return;
      }
      if (labelIds.length > 0 && (hasLabeled || hasUnlabeled)) {
        // User selected specific labels - clear labeled/unlabeled
        onChange({ labelIds, labelFilter: undefined });
        return;
      }

      if (hasLabeled) labelFilter = "labeled";
      else if (hasUnlabeled) labelFilter = "unlabeled";

      onChange({ labelIds, labelFilter });
    },
    [onChange, selectedValues]
  );

  // DD-6: Show skeleton during loading to prevent layout shift
  if (isLoading) {
    return React.createElement("div", {
      className: "w-64 h-10 bg-neutral-200 rounded-md animate-pulse",
    });
  }

  // DD-6: Conditional rendering - hide when no labels available and none selected
  if (!labelCounts && value.labelIds.length === 0 && !value.labelFilter) {
    return null;
  }

  return React.createElement(
    "div",
    { className: "w-64 relative" },
    React.createElement(MultiSelectDropdown, {
      options,
      selectedValues,
      onChange: handleChange,
      placeholder: "Wszystkie etykiety",
      ariaLabel: "Filtruj według etykiety",
    })
  );
});

export const labelFilter: FilterPlugin<LabelFilterValue> = {
  key: "label",
  label: "Etykieta",
  placement: "main",
  order: 6,
  defaultValue: { labelIds: [], labelFilter: undefined },
  Component: LabelFilterComponent,

  isActive(value: LabelFilterValue): boolean {
    return value.labelIds.length > 0 || !!value.labelFilter;
  },

  toUrlParams(value: LabelFilterValue, params: URLSearchParams): void {
    value.labelIds.forEach((id) => params.append("labelId", id));
    if (value.labelFilter) {
      params.set("labelFilter", value.labelFilter);
    }
  },

  fromUrlParams(params: URLSearchParams): LabelFilterValue {
    const labelIds = params.getAll("labelId");
    const labelFilter = params.get("labelFilter") || undefined;
    return { labelIds, labelFilter };
  },

  toSavedView(
    value: LabelFilterValue,
    filters: Partial<SavedViewFilters>
  ): void {
    if (value.labelIds.length > 0) {
      filters.labelIds = value.labelIds;
    }
    if (value.labelFilter) {
      filters.labelFilter = value.labelFilter;
    }
  },

  fromSavedView(filters: SavedViewFilters): LabelFilterValue {
    return {
      labelIds: filters.labelIds ?? [],
      labelFilter: filters.labelFilter ?? undefined,
    };
  },

  toApiParams(value: LabelFilterValue, params: URLSearchParams): void {
    value.labelIds.forEach((id) => params.append("labelId", id));
    if (value.labelFilter) {
      params.set("labelFilter", value.labelFilter);
    }
  },
};
